import { useEffect, useState, useCallback } from 'react';
import { getItemFromSessionStorage, setItemToSessionStorage, ONBOARDING_KEYS } from '../util/localStorage/useSessionStorage';
import { getFileFromIndexedDB } from '../util/localStorage/useIndexedDB';
import { OnBoardingData, OnBoardingStepState, OnBoardingWorkData } from '../dto/EntityRepository';
import { OnBoardingStepType, DimensionsType, SignupArchiveType } from '../enum/EnumRepository';
import { useOnBoardStore } from '../store/onBoardStore';
import { getDefaultOnBoardingData } from '../converter/entityBuilder';
import { baseSteps } from '../../component/onBoarding/onBoardingSteps';
import { getImageDimensions } from '../util/imageUtils';

// 기본 스텝 상태 구조
const getDefaultOnBoardingStepState = (): OnBoardingStepState => ({
  steps: baseSteps,
  currentStep: baseSteps[0],
  maxReachedIndex: 0,
});

interface UseOnBoardingDataResult {
  onBoardingData: OnBoardingData | null;
  updateOnBoardingData: (data: Partial<OnBoardingData>) => void;
  clearOnBoardingData: () => void;
  stepState: OnBoardingStepState;
  goToStep: (step: OnBoardingStepType) => number | null;
  updateStepStateFromPopState: (stepIndex: number) => void;
}

export const useOnBoardingData = (): UseOnBoardingDataResult => {
  const { onBoardingData, setOnBoardingData, updateOnBoardingData, clearOnBoardingData } = useOnBoardStore();

  // sessionStorage에서 스텝 상태를 복원하거나 기본값 사용
  const [stepState, setStepState] = useState<OnBoardingStepState>(() => {
    const storedStepState = getItemFromSessionStorage<OnBoardingStepState>(ONBOARDING_KEYS.STEP);
    if (!storedStepState) return getDefaultOnBoardingStepState();

    const storedCurrentStepIndex = baseSteps.findIndex(
      (step) => step.step === storedStepState.currentStep.step,
    );
    const currentStepIndex = storedCurrentStepIndex >= 0
      ? storedCurrentStepIndex
      : baseSteps.length - 1;

    return {
      steps: baseSteps,
      currentStep: baseSteps[currentStepIndex],
      maxReachedIndex: Math.min(
        Math.max(currentStepIndex, storedStepState.maxReachedIndex),
        baseSteps.length - 1,
      ),
    };
  });

  // 컴포넌트 마운트 시 SessionStorage에서 데이터 로드 (최초 1회만, store 기준으로 판단)
  useEffect(() => {
    const { onBoardingData: currentData } = useOnBoardStore.getState();
    if (currentData) return;

    const loadOnBoardingData = async () => {
      try {
        const storedData = getItemFromSessionStorage<OnBoardingData>(ONBOARDING_KEYS.DATA);
        if (storedData) {
          const restoredData = await restoreFilesFromIndexedDB(storedData);
          setOnBoardingData(restoredData);
        } else {
          setOnBoardingData(getDefaultOnBoardingData());
        }
      } catch (error) {
        console.error('Failed to load onboarding data:', error);
      }
    };
    loadOnBoardingData();
  }, [setOnBoardingData]);

  // 특정 스텝으로 이동 (stepIndex 반환)
  const goToStep = useCallback((step: OnBoardingStepType): number | null => {
    let stepIndex: number | null = null;
    setStepState((prev) => {
      stepIndex = prev.steps.findIndex(eachStep => eachStep.step === step);
      if (stepIndex === -1) return prev;
      
      const targetStep = prev.steps[stepIndex!];
      const newStepState = {
        ...prev,
        currentStep: targetStep,
        maxReachedIndex: Math.max(prev.maxReachedIndex, stepIndex!)
      };
      setItemToSessionStorage(ONBOARDING_KEYS.STEP, newStepState);
      return newStepState;
    });
    return stepIndex;
  }, []);

  // 브라우저 뒤로가기/앞으로가기로 인한 스텝 상태 업데이트
  const updateStepStateFromPopState = useCallback((stepIndex: number) => {
    setStepState((prev) => {
      const targetStep = prev.steps[stepIndex];
      if (!targetStep) return prev;
      
      const newStepState = {
        ...prev,
        currentStep: targetStep,
        maxReachedIndex: Math.max(prev.maxReachedIndex, stepIndex)
      };
      setItemToSessionStorage(ONBOARDING_KEYS.STEP, newStepState);
      return newStepState;
    });
  }, []);

  return {
    onBoardingData: onBoardingData,
    updateOnBoardingData,
    clearOnBoardingData,
    stepState,
    goToStep,
    updateStepStateFromPopState
  };
};

// IndexedDB에서 File 객체들을 복원하는 헬퍼 함수
const restoreFilesFromIndexedDB = async (data: OnBoardingData): Promise<OnBoardingData> => {
  const restored: OnBoardingData = { ...getDefaultOnBoardingData(), ...data };

  if ((restored.signupArchiveType as string) === 'GALLERIST') {
    restored.signupArchiveType = SignupArchiveType.CURATOR;
  }

  if (!data.works || data.works.length === 0) {
    restored.works = [];
    return restored;
  }

  const restoredWorks: OnBoardingWorkData[] = [];

  for (const work of data.works as any[]) {
    // 이미 File 객체가 있으면 그대로 사용
    if (work.file instanceof File) {
      restoredWorks.push(work as OnBoardingWorkData);
      continue;
    }

    // fileId 기반으로 저장된 경우 IndexedDB에서 복원
    if (work.fileId) {
      const restoredFile = await getFileFromIndexedDB(work.fileId);
      if (restoredFile) {
        const dimensions = await getImageDimensions(restoredFile);
        restoredWorks.push({
          file: restoredFile,
          width: dimensions.width,
          height: dimensions.height,
          title: work.title || '',
          size: work.size || { width: '', height: '', depth: '', unit: 0 },
          material: work.material || '',
          prodYear: work.prodYear || '',
          dimensionsType: work.dimensionsType || DimensionsType.FIXED,
        });
        continue;
      }
    }

    // 그 외에는 있는 그대로 사용 (타입 캐스팅)
    restoredWorks.push(work as OnBoardingWorkData);
  }

  restored.works = restoredWorks;
  return restored;
};
